import React from 'react';
import { PasswordInput, TextInput } from '@mantine/core';
import { Button } from '../../../components/Button';
import type { TenantView } from '../../../services/tenants/tenants.service';
import { noSelectionTextInputProps } from '../../PosShellPage/components/receipt/receipt-input.utils';
import classes from '../tenants-page.module.scss';

export type CibusDraft = { userName: string; password: string; restId: string; posId: string };

type TenantCibusBlockProps = {
  tenant: TenantView;
  isUpdating: boolean;
  draft: CibusDraft;
  onDraftChange: (patch: Partial<CibusDraft>) => void;
  onTestLogin: () => void;
  onTestPosWs: () => void;
  onExportMenu: () => void;
  onDisconnect: () => void;
};

const formatStamp = (iso: string | null | undefined): string => {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : ` · ${date.toLocaleString('he-IL')}`;
};

const resolveStatus = (tenant: TenantView): string => {
  if (!tenant.cibusConfigured) {
    return 'לא מוגדר';
  }
  if (tenant.cibusAuthFailedAt) {
    return `אימות נכשל${formatStamp(tenant.cibusAuthFailedAt)}`;
  }
  if (tenant.cibusConnectedAt) {
    return `מחובר${tenant.cibusRestName ? ` · ${tenant.cibusRestName}` : ''}${formatStamp(tenant.cibusConnectedAt)}`;
  }
  return 'מוגדר · טרם אומת';
};

// Super-admin Cibus block: creds drafts (saved by שמור הכל), status, test-login, disconnect.
export const TenantCibusBlock: React.FC<TenantCibusBlockProps> = ({
  tenant,
  isUpdating,
  draft,
  onDraftChange,
  onTestLogin,
  onTestPosWs,
  onExportMenu,
  onDisconnect,
}) => {
  const configured = Boolean(tenant.cibusConfigured);
  const tenderConfigured = Boolean(tenant.cibusRestId && tenant.cibusPosId);
  const statusClass = configured && !tenant.cibusAuthFailedAt ? classes.tenbisStatusConnected : classes.tenbisStatusDisconnected;
  return (
    <div className={classes.tenbisSlot} data-testid={`tenants-cibus-block-${tenant.id}`}>
      <span className={classes.photoLabel}>סיבוס</span>
      <div className={classes.settingsGrid}>
        <TextInput
          label="שם משתמש סיבוס"
          {...noSelectionTextInputProps}
          value={draft.userName}
          onChange={(event) => onDraftChange({ userName: event.currentTarget.value })}
          data-testid={`tenants-cibus-user-${tenant.id}`}
        />
        <PasswordInput
          label="סיסמת סיבוס"
          placeholder={configured ? 'מוגדרת' : 'לא מוגדרת'}
          {...noSelectionTextInputProps}
          value={draft.password}
          onChange={(event) => onDraftChange({ password: event.currentTarget.value })}
          data-testid={`tenants-cibus-password-${tenant.id}`}
        />
        <TextInput
          label="מזהה מסעדה (restId)"
          inputMode="numeric"
          {...noSelectionTextInputProps}
          value={draft.restId}
          onChange={(event) => onDraftChange({ restId: event.currentTarget.value })}
          data-testid={`tenants-cibus-rest-id-${tenant.id}`}
        />
        <TextInput
          label="מזהה קופה (posId)"
          inputMode="numeric"
          {...noSelectionTextInputProps}
          value={draft.posId}
          onChange={(event) => onDraftChange({ posId: event.currentTarget.value })}
          data-testid={`tenants-cibus-pos-id-${tenant.id}`}
        />
      </div>
      <div className={classes.tenbisActions}>
        <span className={statusClass} data-testid={`tenants-cibus-status-${tenant.id}`}>
          {resolveStatus(tenant)}
        </span>
        {configured && (
          <>
            <Button
              variant="save"
              size="sm"
              disabled={isUpdating}
              onClick={onTestLogin}
              dataTestId={`tenants-cibus-test-login-${tenant.id}`}
            >
              בדוק התחברות
            </Button>
            <Button
              variant="save"
              size="sm"
              disabled={isUpdating}
              onClick={onExportMenu}
              dataTestId={`tenants-cibus-menu-export-${tenant.id}`}
            >
              ייצוא תפריט סיבוס
            </Button>
            <Button
              variant="cancel"
              size="sm"
              disabled={isUpdating}
              onClick={onDisconnect}
              dataTestId={`tenants-cibus-disconnect-${tenant.id}`}
            >
              נתק
            </Button>
          </>
        )}
        {tenderConfigured && (
          <Button
            variant="save"
            size="sm"
            disabled={isUpdating}
            onClick={onTestPosWs}
            dataTestId={`tenants-cibus-test-posws-${tenant.id}`}
          >
            בדוק סליקה
          </Button>
        )}
      </div>
    </div>
  );
};
